// comparisonTool.js — Comparison Tool modal content
// Receives the selected module as a prop from moduleMetricsPage.js.
// Rendered inside the shared Modal component.

import React, { useState, useMemo } from "react";
import MOCK_MODULES from "../../../mockData/mockModuleData";

// Helper function to convert time strings to comparable format
const calculateAverageTime = (timeStrings) => {
  let totalHours = 0;
  let count = 0;

  timeStrings.forEach((timeStr) => {
    const match = timeStr.match(/(\d+)d\s+(\d+)h/);
    if (match) {
      const days = parseInt(match[1], 10);
      const hours = parseInt(match[2], 10);
      totalHours += days * 24 + hours;
      count++;
    }
  });

  if (count === 0) return "N/A";

  const avgHours = Math.round(totalHours / count);
  const days = Math.floor(avgHours / 24);
  const hours = avgHours % 24;
  return `${days}d ${hours}h`;
};

const ComparisonTool = ({ module }) => {
  const [selectedComparison, setSelectedComparison] = useState(null);

  // Calculate comparison data dynamically from mockModuleData.js
  const comparisonData = useMemo(() => {
    if (!module) return null;

    // Item Type Comparison: Count modules with same itemNumber
    const itemTypeModules = MOCK_MODULES.filter(
      (m) => m.itemNumber === module.itemNumber,
    );

    // Property Comparison: Count modules in same property
    const propertyModules = MOCK_MODULES.filter(
      (m) => m.property === module.property,
    );

    // Timeline Comparison: Calculate average completion rate for same item type
    const avgCompletionRate =
      itemTypeModules.length > 0
        ? Math.round(
            itemTypeModules.reduce((sum, m) => sum + m.completionRate, 0) /
              itemTypeModules.length,
          )
        : 0;

    // Calculate average time on line for same item type
    const avgTimeOnLine =
      itemTypeModules.length > 0
        ? calculateAverageTime(itemTypeModules.map((m) => m.timeOnLine))
        : "N/A";

    return {
      itemTypeCount: itemTypeModules.length,
      propertyCount: propertyModules.length,
      avgCompletionRate,
      avgTimeOnLine,
      itemTypeModules,
      propertyModules,
    };
  }, [module]);

  const handleComparisonClick = (type) => {
    setSelectedComparison(type);
    console.log(`Activating ${type} comparison for module:`, module.workOrder);

    // Log comparison insights
    if (type === "item-type" && comparisonData) {
      console.log(
        `Found ${comparisonData.itemTypeCount} modules with item type ${module.itemNumber}`,
      );
    } else if (type === "property" && comparisonData) {
      console.log(
        `Found ${comparisonData.propertyCount} modules in ${module.property}`,
      );
    } else if (type === "timeline" && comparisonData) {
      console.log(
        `Average completion rate for ${module.itemNumber}: ${comparisonData.avgCompletionRate}%`,
      );
    }
  };

  // Null safety check
  if (!module || !comparisonData) {
    return (
      <div className="mm-comparison-content">
        <p className="modal-description">No module selected for comparison</p>
      </div>
    );
  }

  const { workOrder, itemNumber, property, completionRate, timeOnLine } =
    module;

  return (
    <div className="mm-comparison-content">
      <p className="modal-description">
        Compare {workOrder} against similar modules in the same batch or
        property. Click a comparison method below to activate the tool.
      </p>

      <div className="modal-section">
        <h3>Suggested Comparison Methods</h3>
        <div className="mm-comparison-options">
          {/* Item Type Comparison */}
          <button
            className={`mm-comparison-card ${
              selectedComparison === "item-type"
                ? "mm-comparison-card--active"
                : ""
            }`}
            onClick={() => handleComparisonClick("item-type")}
          >
            <div className="mm-comparison-card-header">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <rect x="3" y="3" width="7" height="7" />
                <rect x="14" y="3" width="7" height="7" />
                <rect x="14" y="14" width="7" height="7" />
                <rect x="3" y="14" width="7" height="7" />
              </svg>
              <h4>Item Type Comparison</h4>
            </div>
            <p>
              Compare all modules in item type: <strong>{itemNumber}</strong>{" "}
              side by side. Useful for identifying outliers in completion rate
              or time on line within the same item type.
            </p>
            <div className="mm-comparison-tag mm-comparison-tag--recommended">
              Recommended
            </div>
          </button>

          {/* Property Comparison */}
          <button
            className={`mm-comparison-card ${
              selectedComparison === "property"
                ? "mm-comparison-card--active"
                : ""
            }`}
            onClick={() => handleComparisonClick("property")}
          >
            <div className="mm-comparison-card-header">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                <polyline points="9 22 9 12 15 12 15 22" />
              </svg>
              <h4>Property Comparison</h4>
            </div>
            <p>
              Compare all modules across <strong>{property}</strong>. Highlights
              property-wide trends in station dwell time and rework rates.
            </p>
            <div className="mm-comparison-tag mm-comparison-tag--suggested">
              Suggested
            </div>
          </button>

          {/* Timeline Comparison */}
          <button
            className={`mm-comparison-card ${
              selectedComparison === "timeline"
                ? "mm-comparison-card--active"
                : ""
            }`}
            onClick={() => handleComparisonClick("timeline")}
          >
            <div className="mm-comparison-card-header">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
              <h4>Timeline Comparison</h4>
            </div>
            <p>
              Compare this module's station-by-station timeline against the
              average for its item type (<strong>{itemNumber}</strong>).
              Identifies where time is being gained or lost.
            </p>
            <div className="mm-comparison-tag mm-comparison-tag--available">
              Available
            </div>
          </button>
        </div>

        {/* Show selected comparison feedback */}
        {selectedComparison && (
          <div className="mm-comparison-active-notice">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <circle cx="12" cy="12" r="10" />
              <polyline points="9 12 12 15 16 10" />
            </svg>
            <span>
              <strong>
                {selectedComparison === "item-type" && "Item Type Comparison"}
                {selectedComparison === "property" && "Property Comparison"}
                {selectedComparison === "timeline" && "Timeline Comparison"}
              </strong>{" "}
              activated for {workOrder}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default ComparisonTool;
